import api from '@/lib/api';
import type { AccessGrant, CreateAccessGrantInput, UpdateAccessGrantInput } from '@hygge/shared';
import { CACHE_TIME } from '@hygge/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accessTypesKeys } from './use-access-types';
import { inventoryKeys } from './use-assets';

export const accessGrantsKeys = {
  all: ['access-grants'] as const,
  lists: () => [...accessGrantsKeys.all, 'list'] as const,
  byEmployee: (employeeId: string) => [...accessGrantsKeys.lists(), { employeeId }] as const,
};

async function getEmployeeAccessGrants(employeeId: string): Promise<AccessGrant[]> {
  const { data } = await api.get<AccessGrant[]>(`/access-grants/employee/${employeeId}`);
  return data;
}

async function createAccessGrant(input: CreateAccessGrantInput): Promise<AccessGrant> {
  const { data } = await api.post<AccessGrant>('/access-grants', input);
  return data;
}

async function updateAccessGrant(
  id: string,
  input: UpdateAccessGrantInput,
): Promise<AccessGrant> {
  const { data } = await api.put<AccessGrant>(`/access-grants/${id}`, input);
  return data;
}

async function deleteAccessGrant(id: string): Promise<void> {
  await api.delete(`/access-grants/${id}`);
}

async function revokeAccessGrant(id: string): Promise<AccessGrant> {
  const { data } = await api.patch<AccessGrant>(`/access-grants/${id}/revoke`);
  return data;
}

export function useEmployeeAccessGrants(employeeId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: accessGrantsKeys.byEmployee(employeeId),
    queryFn: () => getEmployeeAccessGrants(employeeId),
    enabled: !!employeeId && (options?.enabled ?? true),
    staleTime: CACHE_TIME.SHORT,
  });
}

// Backs both the Inventory create dialog (has its own inline error) and the
// employee-profile Access tab (no handling of its own) — takes an opt-in
// flag instead of a fixed meta so each caller gets the right behavior.
export function useCreateAccessGrant(opts?: { suppressGlobalToast?: boolean }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAccessGrant,
    meta: { suppressGlobalToast: opts?.suppressGlobalToast ?? false },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: accessGrantsKeys.byEmployee(variables.employeeId),
      });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useUpdateAccessGrant(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAccessGrantInput & { id: string }) =>
      updateAccessGrant(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}

export function useInventoryUpdateAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateAccessGrantInput & { id: string }) =>
      updateAccessGrant(id, input),
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.all });
    },
  });
}

export function useInventoryDeleteAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccessGrant,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.all });
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useInventoryRevokeAccessGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeAccessGrant,
    meta: { suppressGlobalToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.all });
    },
  });
}

export function useDeleteAccessGrant(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccessGrant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
      queryClient.invalidateQueries({ queryKey: accessTypesKeys.all });
    },
  });
}

export function useRevokeAccessGrant(employeeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: revokeAccessGrant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accessGrantsKeys.byEmployee(employeeId) });
      queryClient.invalidateQueries({ queryKey: inventoryKeys.all });
    },
  });
}
